import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Mail, Phone, MapPin, CheckCircle, Calendar } from 'lucide-react';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    monthlyClosings: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div data-testid="contact-page" className="min-h-screen bg-[#0F0F0F]">
        <section className="pt-32 pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-xl mx-auto text-center"
              data-testid="contact-success-state"
            >
              <div className="bg-[#1A1A1A] border border-[#C9A227]/50 p-12">
                <CheckCircle className="w-16 h-16 text-[#C9A227] mx-auto mb-6" strokeWidth={1} />
                <h2 className="font-serif text-3xl text-white mb-4">Thank You!</h2>
                <p className="text-neutral-400 mb-8">
                  Your message has been received. A member of our team will reach out 
                  within 24 hours to discuss how Better Closings can help elevate your 
                  post-close experience.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-8 py-4 font-bold text-sm tracking-wider uppercase transition-all duration-300"
                  data-testid="send-another-message-btn"
                >
                  Send Another Message
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#0F0F0F]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="h-[2px] w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Get In Touch</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Contact Us & <span className="text-[#E5D5A5]">Book a Demo</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              Ready to elevate your post-close experience? Fill out the form below 
              or book a demo directly. We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Calendar Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-2xl text-white mb-8">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-neutral-300 text-sm">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="bg-[#141414] border-white/10 text-white focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] rounded-none h-12"
                      placeholder="John Smith"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-neutral-300 text-sm">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="bg-[#141414] border-white/10 text-white focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] rounded-none h-12"
                      placeholder="john@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-neutral-300 text-sm">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="bg-[#141414] border-white/10 text-white focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] rounded-none h-12"
                      placeholder="(555) 123-4567"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-neutral-300 text-sm">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="bg-[#141414] border-white/10 text-white focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] rounded-none h-12"
                      placeholder="Your Company"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-neutral-300 text-sm">Your Role</Label>
                    <Select onValueChange={(value) => handleSelectChange('role', value)}>
                      <SelectTrigger 
                        className="bg-[#141414] border-white/10 text-white focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] rounded-none h-12"
                        data-testid="contact-role-select"
                      >
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="loan-officer">Loan Officer</SelectItem>
                        <SelectItem value="mortgage-broker">Mortgage Broker</SelectItem>
                        <SelectItem value="branch-manager">Branch Manager</SelectItem>
                        <SelectItem value="lender-partner">Lender Partner</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monthlyClosings" className="text-neutral-300 text-sm">Monthly Closings</Label>
                    <Select onValueChange={(value) => handleSelectChange('monthlyClosings', value)}>
                      <SelectTrigger 
                        className="bg-[#141414] border-white/10 text-white focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] rounded-none h-12"
                        data-testid="contact-closings-select"
                      >
                        <SelectValue placeholder="Avg monthly closings" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="1-5">1-5 loans</SelectItem>
                        <SelectItem value="6-10">6-10 loans</SelectItem>
                        <SelectItem value="11-25">11-25 loans</SelectItem>
                        <SelectItem value="26-50">26-50 loans</SelectItem>
                        <SelectItem value="50+">50+ loans</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-neutral-300 text-sm">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="bg-[#141414] border-white/10 text-white focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] rounded-none min-h-[120px] resize-none"
                    placeholder="Tell us about your needs or ask any questions..."
                    data-testid="contact-message-textarea"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-8 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </motion.div>

            {/* Calendar Embed & Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-2xl text-white mb-8">Book a Demo</h2>
              
              {/* Calendar Placeholder */}
              <div 
                className="bg-[#1A1A1A] border border-white/5 p-8 mb-8 min-h-[400px] flex flex-col items-center justify-center"
                data-testid="calendar-embed-placeholder"
              >
                <Calendar className="w-16 h-16 text-[#C9A227] mb-6" strokeWidth={1} />
                <h3 className="font-serif text-xl text-white mb-3">Schedule Your Demo</h3>
                <p className="text-neutral-400 text-sm text-center mb-6 max-w-sm">
                  Choose a convenient time for a personalized walkthrough of 
                  Better Closings and how it can benefit your business.
                </p>
                <div className="text-neutral-500 text-xs border border-dashed border-white/20 px-4 py-2">
                  Calendly embed will appear here
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-[#1A1A1A] border border-white/5 p-8">
                <h3 className="font-serif text-lg text-white mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <a
                    href="mailto:info@betterclosings.com"
                    className="flex items-center text-neutral-400 hover:text-[#C9A227] transition-colors"
                    data-testid="contact-info-email"
                  >
                    <Mail size={18} className="mr-4 text-[#C9A227]" />
                    info@betterclosings.com
                  </a>
                  <a
                    href="tel:+18001234567"
                    className="flex items-center text-neutral-400 hover:text-[#C9A227] transition-colors"
                    data-testid="contact-info-phone"
                  >
                    <Phone size={18} className="mr-4 text-[#C9A227]" />
                    1-800-123-4567
                  </a>
                  <div className="flex items-center text-neutral-400">
                    <MapPin size={18} className="mr-4 text-[#C9A227]" />
                    Serving Nationwide
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-white/5">
                  <p className="text-neutral-500 text-sm">
                    Business Hours: Monday - Friday, 9 AM - 6 PM EST
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
